import React, { useState, useCallback, useMemo, useEffect } from "react";
import _ from "lodash";
import { useSelector } from "react-redux";
import { useRouteMatch } from "react-router-dom";

import { Table, Button } from "components";
import ReportTable from "containers/underwriting/reports/reportTable";
import {
	getResultTableData,
	LABELS_MATCH_RESULT,
	matchDataMapper,
	sortPossibleMatches,
} from "mappers";
import {
	useDownloadMatchDocumentMutation,
	useMatchAuroraOutcomeFieldMutation,
	useMatchReRunMutation,
	useMatchResultQuery,
} from "queries";
import {
	getCurrentUserAccessToken,
	getCurrentUserId,
} from "store/user/user.reducer";
import { UnderwritingTabs } from "types";

import * as table from "./tableColumns";
import CollapseSection from "../components/collapseSection";

import styles from "./matchReport.module.scss";

type MatchReportProps = {
	handleViewClick: Function;
	appId: string | undefined | any;
};

const INITIAL_SORT_BY = { id: "matched_fields_length", desc: true };

const MatchReport = ({ appId, handleViewClick }: MatchReportProps) => {
	const currentUserId = useSelector(getCurrentUserId);
	const token: string = useSelector(getCurrentUserAccessToken);

	const match: any = useRouteMatch();

	const isMerchantPage = useMemo(
		() => match?.url?.startsWith("/merchant/"),
		[match?.url],
	);

	const [selectedMerchantMatches, setSelectedMerchantMatches] = useState<
		number[]
	>([]);
	const [selectedInquiryMatches, setSelectedInquiryMatches] = useState<
		number[]
	>([]);
	const [sortedMerchantMatches, setSorterMerchantMatches] = useState<
		{ [key: string]: any }[]
	>([]);
	const [sortedInquiryMatches, setSorterInquiryMatches] = useState<
		{ [key: string]: any }[]
	>([]);

	const {
		data: matchResult,
		isLoading: isLoadingDataMatch,
		refetch: dataMatchRefetch,
	} = useMatchResultQuery({
		id: appId,
	});

	useEffect(() => {
		const matches = !_.isEmpty(matchResult?.rerun?.merchant_matches)
			? matchResult?.rerun?.merchant_matches
			: matchResult?.original?.merchant_matches;

		setSorterMerchantMatches(
			sortPossibleMatches({
				sortBy: [INITIAL_SORT_BY],
				matches,
			}),
		);
	}, [
		matchResult?.original?.merchant_matches,
		matchResult?.rerun?.merchant_matches,
	]);

	useEffect(() => {
		const matches = !_.isEmpty(matchResult?.rerun?.inquiry_matches)
			? matchResult?.rerun?.inquiry_matches
			: matchResult?.original?.inquiry_matches;

		setSorterInquiryMatches(
			sortPossibleMatches({
				sortBy: [INITIAL_SORT_BY],
				matches,
			}),
		);
	}, [
		matchResult?.original?.inquiry_matches,
		matchResult?.rerun?.inquiry_matches,
	]);

	const [possibleMerchantMatchesTableColumns] = useState([
		table.dateColumn({ title: "Date of Match Entry" }),
		table.mainColumn({
			name: "acquirer_id",
			title: "Acquirer ID",
			disableSortBy: true,
		}),
		table.mainColumn({
			name: "legal_name",
			title: "Legal Name(Merchant Name)",
		}),
		table.mainColumn({
			name: "dba_name",
			title: "Doing Business As Name(DBA)",
			disableSortBy: true,
		}),
		table.mainColumn({
			name: "city",
			title: "City",
			disableSortBy: true,
		}),
		table.stateColumn(),
		table.mainColumn({
			name: "reason_code",
			title: "Reason Code",
			disableSortBy: true,
		}),
		table.mainColumn({
			name: "reason_code_description",
			title: "Reason Code Description",
			disableSortBy: true,
		}),
		table.numberOfFieldsMatched(),
		table.fieldTypesColumn(),
	]);

	const [possibleInquiryMatchesTableColumns] = useState([
		table.dateColumn({ title: "Date of Merchant Inquiry" }),
		table.mainColumn({
			name: "legal_name",
			title: "Legal Name(Merchant Name)",
		}),
		table.mainColumn({
			name: "dba_name",
			title: "Doing Business As Name(DBA)",
			disableSortBy: true,
		}),
		table.mainColumn({
			name: "city",
			title: "City",
			disableSortBy: true,
		}),
		table.stateColumn(),
		table.numberOfFieldsMatched(),
		table.fieldTypesColumn(),
	]);

	const rowsNotChecked =
		_.isEmpty(selectedMerchantMatches) && _.isEmpty(selectedInquiryMatches);

	const handleMerchantMatchesSelect = rows => {
		const selectedMerchantMatchesIds = rows
			.filter(row => row.isSelected)
			.map(row => row.original.id);

		!_.isEqual(selectedMerchantMatches, selectedMerchantMatchesIds) &&
			setSelectedMerchantMatches(selectedMerchantMatchesIds);
	};

	const handleInquiryMatchesSelect = rows => {
		const selectedInquiryMatchesIds = rows
			.filter(row => row.isSelected)
			.map(row => row.original.id);

		!_.isEqual(selectedInquiryMatches, selectedInquiryMatchesIds) &&
			setSelectedInquiryMatches(selectedInquiryMatchesIds);
	};

	const onViewClick = () => {
		if (handleViewClick) {
			handleViewClick(selectedMerchantMatches, selectedInquiryMatches);
		}
	};

	const {
		mutate: submitMatchAuroraOutcome,
		isLoading: isLoadingMatchAuroraOutcome,
	} = useMatchAuroraOutcomeFieldMutation();

	const submitAuroraOutcomeHandler = useCallback(
		values => {
			submitMatchAuroraOutcome({
				id: appId,
				values,
				onSuccessHandler: dataMatchRefetch,
			});
		},
		[appId, dataMatchRefetch, submitMatchAuroraOutcome],
	);

	const {
		mutate: downloadMatchDocument,
		isLoading: isLoadingDownloadMatchDocument,
	} = useDownloadMatchDocumentMutation();

	const downloadDocument = useCallback(
		() =>
			downloadMatchDocument({
				id: appId,
				token: token,
			}),
		[appId, downloadMatchDocument, token],
	);

	const matchReportResult = getResultTableData(
		matchDataMapper({
			data: matchResult?.original,
			auroraOutcome: matchResult?.original?.outcome,
		}),
		matchDataMapper({
			data: matchResult?.rerun,
			auroraOutcome: matchResult?.rerun?.outcome,
		}),
		LABELS_MATCH_RESULT,
	);

	const { mutate: reRunMatch, isLoading: isLoadingReRunMatch } =
		useMatchReRunMutation();

	const handleReRunMatch = () => {
		reRunMatch({ id: appId, onSuccessHandler: dataMatchRefetch });
	};

	const handleMerchantMatchesSortChange = useCallback(
		({ sortBy }) => {
			if (!_.isEmpty(sortBy)) {
				setSorterMerchantMatches(value =>
					sortPossibleMatches({
						sortBy,
						matches: value,
					}),
				);
			}
		},
		[setSorterMerchantMatches],
	);

	const handleInquiryMatchesSortChange = useCallback(
		({ sortBy }) => {
			if (!_.isEmpty(sortBy)) {
				setSorterInquiryMatches(value =>
					sortPossibleMatches({
						sortBy,
						matches: value,
					}),
				);
			}
		},
		[setSorterInquiryMatches],
	);

	const isRerun = useMemo(() => !!matchResult?.rerun, [matchResult?.rerun]);

	const canEdit =
		!isMerchantPage &&
		match?.params?.tab !== UnderwritingTabs.Unassigned.name &&
		match?.params?.tab !== UnderwritingTabs.Historical.name &&
		match?.params?.tab !== UnderwritingTabs.QAUnassigned.name &&
		match?.params?.tab !== UnderwritingTabs.QAComplete.name &&
		matchResult?.underwriter_id &&
		matchResult?.underwriter_id === currentUserId;

	const editableFields = useMemo(() => {
		let fields = {};
		if (canEdit) {
			fields = {
				...fields,
				outcome: {
					submitHandler: submitAuroraOutcomeHandler,
					isLoading: isLoadingMatchAuroraOutcome,
					reactSelectProps: { menuPlacement: "top" },
				},
			};
		}
		return fields;
	}, [canEdit, isLoadingMatchAuroraOutcome, submitAuroraOutcomeHandler]);

	return (
		<>
			<ReportTable
				data={matchReportResult}
				isRerun={isRerun}
				isLoading={isLoadingDataMatch}
				editableFields={editableFields}
			/>
			{canEdit && (
				<Button
					type="button"
					value="re-run match"
					className="mt-4"
					onClick={handleReRunMatch}
					isLoading={isLoadingDataMatch || isLoadingReRunMatch}
				/>
			)}
			<hr className="line w100" />
			<CollapseSection
				header="Possible Merchant Matches"
				recordsCount={sortedMerchantMatches?.length}
				collapseClassName={`${styles.containerCollapse} mb-4`}
				loading={isLoadingDataMatch}
			>
				<Table
					columns={possibleMerchantMatchesTableColumns}
					data={sortedMerchantMatches || []}
					initialState={{
						sortBy: [INITIAL_SORT_BY],
					}}
					handleRowSelect={handleMerchantMatchesSelect}
					handleSortChange={handleMerchantMatchesSortChange}
					classNameHeader={styles.tableHeader}
					hideFooter
				/>
			</CollapseSection>

			<CollapseSection
				header="Possible Inquiry Matches"
				recordsCount={sortedInquiryMatches?.length}
				collapseClassName={styles.containerCollapse}
				loading={isLoadingDataMatch}
			>
				<Table
					columns={possibleInquiryMatchesTableColumns}
					data={sortedInquiryMatches || []}
					initialState={{
						sortBy: [INITIAL_SORT_BY],
					}}
					handleSortChange={handleInquiryMatchesSortChange}
					classNameHeader={styles.tableHeader}
					handleRowSelect={handleInquiryMatchesSelect}
					hideFooter
				/>
			</CollapseSection>

			<footer className={styles.buttonsWrapper}>
				<Button
					onClick={onViewClick}
					className={styles.btn}
					disabled={rowsNotChecked}
					value="View"
				/>
				<Button
					onClick={downloadDocument}
					variant="outline-primary"
					value="Download"
					className={styles.btn}
					isLoading={
						isLoadingDownloadMatchDocument || isLoadingReRunMatch
					}
				/>
			</footer>
		</>
	);
};

export default MatchReport;
